// Conversion between what POSfast edits on screen and what the database holds.
//
//   screen : invoice (header fields) + cartItems (one row per line)
//   base   : Documents (header)      + DocumentDetails (lines)
//
// Both directions live here on purpose. When the two halves sit in different
// components they drift apart, which is exactly what happened before: the cart
// was edited while a second, separately built list was the one being saved.

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}
const round2 = (v) => Math.round(num(v) * 100) / 100

// POS is documentSide 2, so the quantity sits in itemPcsMinus. Other sides use
// their own column, so the side decides which one is read.
const qtyOf = (detail, documentSide) => {
  const side = parseInt(documentSide, 10)
  if (side === 1) { return num(detail.itemPcsPlus) }
  if (side === 3) { return num(detail.itemPcsNeutral) }
  return num(detail.itemPcsMinus)
}

/**
 * DocumentDetails -> cart rows.
 *
 * basePrice is the NET unit price: the cart column is "Neto cijena" and the total
 * adds the VAT on top of it, so itemPrice is used and not itemPriceWithVAT.
 * A per unit discount is derived from the difference between the plain price and
 * the discounted one, which is how the detail row stores it.
 */
export const detailsToCart = (details, documentSide) => {
  if (!details || !details.length) { return [] }
  return details.map(d => {
    const basePrice = round2(d.itemPrice)
    const discounted = num(d.itemDiscountPrice)
    const discount = discounted > 0 && discounted < basePrice ? round2(basePrice - discounted) : 0
    return {
      name: d.itemName || '',
      qty: qtyOf(d, documentSide),
      basePrice: basePrice,
      taxRate: round2(num(d.itemVAT1Percentage) / 100),
      discount: discount,
      targetGross: round2(d.itemFullTotal),
      // kept so a saved line can be written back as the same article
      dbId: d.id || null,
      itemID: d.itemID || null,
      ItemCode: d.ItemCode || null,
      ItemEANCode: d.ItemEANCode || null,
      itemUnit: d.itemUnit || '',
      // roba ili usluga - ponuda grupira stavke po tome
      itemService: d.itemService === true,
      itemGroup: d.itemGroup || null,
      PotpunaSifra: d.PotpunaSifra || null
    }
  })
}

/**
 * Document header -> the invoice object the form binds to.
 * Only the fields POSfast actually shows are filled in; the header itself stays
 * the source for everything else.
 */
export const documentToInvoice = (document, current) => {
  const doc = document || {}
  const inv = Object.assign({}, current || {})
  inv.date = doc.documentDate ? String(doc.documentDate).substr(0, 10) : inv.date
  inv.time = doc.documentTime ? String(doc.documentTime).substr(0, 5) : inv.time
  inv.customerName = doc.partnerName || ''
  inv.companyName = doc.privatePerson ? '' : (doc.partnerName || '')
  inv.vatId = doc.partnerVATID || ''
  inv.email = doc.partneremail || doc.email || ''
  inv.comment = doc.documentDescription4 || ''
  inv.applyVat = doc.documentTotalVAT1Amount !== undefined
    ? num(doc.documentTotalVAT1Amount) > 0 || num(doc.documentTotalVAT2Amount) > 0
    : true
  return inv
}

// The amount is written into the field that belongs to the chosen payment, and
// documentStatus carries the written form. Reading it back is the other way round:
// whichever field holds an amount tells you what was selected. Combinations of the
// three are possible later without changing the shape of this.
export const PAYMENT_TYPES = [
  {value: 'cash',  field: 'documentPOSCash',  status: 'GOTOVINA',            label: 'Gotovina',      icon: 'mdi-cash'},
  {value: 'card',  field: 'documentPOSCard',  status: 'KARTICA',             label: 'Kartica',       icon: 'mdi-credit-card'},
  {value: 'bank',  field: 'documentPOSOther', status: 'TRANSAKCIJSKI RACUN', label: 'Žiro račun',    icon: 'mdi-bank'}
]

export const paymentByValue = (value) => PAYMENT_TYPES.find(p => p.value === value) || null

export const documentToPayment = (document) => {
  const doc = document || {}
  // the field that carries an amount is the payment that was used
  const used = PAYMENT_TYPES.find(p => num(doc[p.field]) > 0)
  const cash = num(doc.documentPOSCash)
  return {
    paymentMethod: used ? used.value : null,
    // only cash needs the received amount for the change
    cashTendered: cash > 0 ? round2(cash) : null
  }
}

export const documentToCustomerType = (document) =>
  (document && document.privatePerson) ? 'private' : 'company'


// Which quantity column a side writes to - mirror of qtyOf above.
const setQty = (detail, documentSide, qty) => {
  const side = parseInt(documentSide, 10)
  detail.itemPcsPlus = side === 1 ? qty : 0
  detail.itemPcsMinus = side === 2 || isNaN(side) ? qty : 0
  detail.itemPcsNeutral = side === 3 ? qty : 0
  return detail
}

/**
 * Cart rows -> DocumentDetails, built fresh at save time from what is on screen.
 *
 * The discount convention is the one the existing rows use:
 *   itemDiscountPrice   net unit price AFTER the discount
 *   itemDiscountAmount1 discount for the WHOLE row (unit discount x quantity)
 *   itemBaseAmountNoVAT itemDiscountPrice x quantity
 *
 * @param {Array}    cartItems   rows as edited on screen
 * @param {Object}   document    the header, only documentSide is read
 * @param {Function} makeBase    returns a fresh detail with all the defaults filled in
 */
export const cartToDetails = (cartItems, document, makeBase) => {
  if (!cartItems || !cartItems.length) { return [] }
  const side = document ? document.documentSide : 2

  return cartItems.map(row => {
    const detail = makeBase ? makeBase() : {}

    const qty = num(row.qty)
    const basePrice = num(row.basePrice)          // net unit price, before discount
    const discount = num(row.discount)            // net discount per unit
    const taxRate = num(row.taxRate)              // 0.25 for 25 %

    const netUnit = round2(basePrice - discount)
    const rowNet = round2(netUnit * qty)
    const rowVat = round2(rowNet * taxRate)
    const rowGross = round2(rowNet + rowVat)

    detail.itemName = row.name || ''
    if (row.itemID) { detail.itemID = row.itemID }
    if (row.ItemCode) { detail.ItemCode = row.ItemCode }
    if (row.ItemEANCode) { detail.ItemEANCode = row.ItemEANCode }
    if (row.itemUnit) { detail.itemUnit = row.itemUnit }
    detail.itemService = row.itemService === true
    if (row.itemGroup) { detail.itemGroup = row.itemGroup }
    if (row.PotpunaSifra) { detail.PotpunaSifra = row.PotpunaSifra }
    // the row id is deliberately not carried over: saveitems() deletes the old lines
    // and inserts new ones, so the database assigns fresh ids

    setQty(detail, side, qty)

    detail.itemPrice = basePrice
    detail.itemPriceWithVAT = round2(basePrice * (1 + taxRate))
    detail.itemDiscountPercentage1 = basePrice > 0 ? round2((discount / basePrice) * 100) : 0
    detail.itemDiscountAmount1 = round2(discount * qty)
    detail.itemDiscountPrice = netUnit

    detail.itemBaseAmountNoVAT = rowNet
    detail.itemVAT1Percentage = round2(taxRate * 100)
    detail.itemVAT1Amount = rowVat
    detail.itemAmountWithVAT = rowGross
    detail.itemFullTotal = rowGross

    return detail
  })
}

// Header fields POSfast owns that are not already written by the partner pickers.
export const invoiceToDocument = (invoice, document, cashTendered, totalAmount) => {
  const doc = document || {}
  const inv = invoice || {}
  if (inv.email) { doc.partneremail = inv.email }
  if (inv.comment) { doc.documentDescription4 = inv.comment }

  // one field per payment - the amount goes into the chosen one, the others are
  // cleared so that reading it back can never be ambiguous
  const chosen = paymentByValue(inv.paymentMethod) || paymentByValue('cash')
  const total = round2(totalAmount)
  PAYMENT_TYPES.forEach(p => { doc[p.field] = 0 })
  // for cash the received amount is what is recorded, for the rest it is the total
  doc[chosen.field] = chosen.value === 'cash' && num(cashTendered) > 0
    ? round2(cashTendered)
    : total
  doc.documentStatus = chosen.status
  return doc
}

export default { detailsToCart, cartToDetails, documentToInvoice, invoiceToDocument, documentToPayment, documentToCustomerType, PAYMENT_TYPES, paymentByValue }
